import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type {
  MovementType,
  MovementSubtype,
  CreateMovementSubtypeInput,
  UpdateMovementSubtypeInput,
  UpdateMovementSubtypeStatusInput,
  MovementSubtypeErrorCode,
  ApiEnvelope,
} from "@/domain/types";
import axios from "axios";

export function createMovementSubtypeError(code: MovementSubtypeErrorCode): Error {
  const err = new Error(code);
  err.name = "MovementSubtypeError";
  return err;
}

export function isMovementSubtypeError(err: unknown): err is Error & { message: MovementSubtypeErrorCode } {
  return err instanceof Error && err.name === "MovementSubtypeError";
}

function mapError(statusCode: number, data?: unknown): MovementSubtypeErrorCode {
  if (statusCode === 409) return "DUPLICATE_CODE";
  if (statusCode === 400) {
    const msg = typeof data === "object" && data !== null ? (data as Record<string, unknown>).message : "";
    if (typeof msg === "string" && msg.includes("inactive")) return "TYPE_INACTIVE";
    return "VALIDATION";
  }
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listMovementTypes(): Promise<MovementType[]> {
  const response = await warehouseClient.get<ApiEnvelope<MovementType[]>>("/movement-type");
  return response.data.data ?? [];
}

export async function listMovementSubtypes(): Promise<MovementSubtype[]> {
  const response = await warehouseClient.get<ApiEnvelope<MovementSubtype[]>>("/movement-subtype");
  return response.data.data ?? [];
}

export async function createMovementSubtype(input: CreateMovementSubtypeInput): Promise<void> {
  try {
    await warehouseClient.post("/movement-subtype", input);
  } catch (err) {
    const code = extractStatusCode(err);
    const data = axios.isAxiosError(err) ? err.response?.data : undefined;
    throw createMovementSubtypeError(mapError(code, data));
  }
}

export async function updateMovementSubtype(input: UpdateMovementSubtypeInput): Promise<void> {
  try {
    await warehouseClient.patch("/movement-subtype", input);
  } catch (err) {
    const code = extractStatusCode(err);
    throw createMovementSubtypeError(mapError(code));
  }
}

export async function updateMovementSubtypeStatus(input: UpdateMovementSubtypeStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/movement-subtype/status", input);
  } catch (err) {
    const code = extractStatusCode(err);
    throw createMovementSubtypeError(mapError(code));
  }
}
